import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { ArrowLeft, Trash, ShieldCheck, User, ToggleLeft, ToggleRight, ChartBar, Key, Plus, Minus, UploadSimple, DownloadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage({ onBack }) {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetResult, setResetResult] = useState(null);
  const [creditEditing, setCreditEditing] = useState(null);
  const [creditValue, setCreditValue] = useState('');
  const [importingSpaza, setImportingSpaza] = useState(false);
  const [importingProps, setImportingProps] = useState(false);
  const [creditRequests, setCreditRequests] = useState([]);
  const [passwordResetRequests, setPasswordResetRequests] = useState([]);
  const [invoiceRequests, setInvoiceRequests] = useState([]);
  const [grantValue, setGrantValue] = useState('10');
  const spazaFileRef = useRef(null);
  const propsFileRef = useRef(null);

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/users`, { withCredentials: true });
      setUsers(data);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditRequests = async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/credit-requests`, { withCredentials: true });
      setCreditRequests(data);
    } catch (err) {
      console.error('Failed to load credit requests:', err);
    }
  };

  const fetchPasswordResetRequests = async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/password-reset-requests`, { withCredentials: true });
      setPasswordResetRequests(data);
    } catch (err) {
      console.error('Failed to load password reset requests:', err);
    }
  };

  const fetchInvoiceRequests = async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/invoice-requests`, { withCredentials: true });
      setInvoiceRequests(data);
    } catch (err) {
      console.error('Failed to load invoice requests:', err);
    }
  };

  useEffect(() => { fetchUsers(); fetchCreditRequests(); fetchPasswordResetRequests(); fetchInvoiceRequests(); }, []);

  const toggleRole = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    try {
      await axios.put(`${API}/api/auth/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      setUsers(prev => prev.map(u => u._id === userId ? { ...u, role: newRole } : u));
      toast.success(`Role updated to ${newRole}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const toggleStatus = async (userId, isActive) => {
    try {
      await axios.put(`${API}/api/auth/users/${userId}/status`, { is_active: !isActive }, { withCredentials: true });
      setUsers(prev => prev.map(u => u._id === userId ? { ...u, is_active: !isActive } : u));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/auth/users/${userId}`, { withCredentials: true });
      setUsers(prev => prev.filter(u => u._id !== userId));
      toast.success('User deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const resetPassword = async (userId, email) => {
    if (!window.confirm(`Reset password for ${email}?`)) return;
    try {
      const { data } = await axios.put(`${API}/api/auth/users/${userId}/reset-password`, {}, { withCredentials: true });
      setResetResult({ userId, tempPassword: data.temp_password });
      toast.success('Password reset successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const saveCredits = async (userId) => {
    const val = parseInt(creditValue, 10);
    if (isNaN(val) || val < 0) { toast.error('Enter a valid credit amount'); return; }
    try {
      await axios.put(`${API}/api/auth/users/${userId}/credits`, { credits: val }, { withCredentials: true });
      setUsers(prev => prev.map(u => u._id === userId ? { ...u, credits: val } : u));
      setCreditEditing(null);
      toast.success(`Credits updated to ${val}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update credits');
    }
  };

  const handleSpazaImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportingSpaza(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/import-spaza`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true });
      toast.success(`Imported ${data.inserted} spaza shops (${data.skipped} skipped). Total: ${data.total_spaza_clusters}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import spaza shops');
    } finally {
      setImportingSpaza(false);
      e.target.value = '';
    }
  };

  const handlePropsImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportingProps(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/import-properties-admin`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true });
      toast.success(`Imported ${data.inserted} properties (${data.skipped} skipped). Total: ${data.total_properties}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import properties');
    } finally {
      setImportingProps(false);
      e.target.value = '';
    }
  };

  const downloadTemplate = async (type) => {
    try {
      const response = await axios.get(`${API}/api/templates/${type}`, { responseType: 'blob', withCredentials: true });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = type === 'spaza' ? 'spaza_shop_template.xlsx' : 'property_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const approveCreditRequest = async (requestId, userId) => {
    const credits = parseInt(grantValue, 10);
    if (isNaN(credits) || credits <= 0) { toast.error('Enter valid credit amount'); return; }
    try {
      await axios.put(`${API}/api/auth/credit-requests/${requestId}/approve`, { credits }, { withCredentials: true });
      setCreditRequests(prev => prev.filter(r => r._id !== requestId));
      setUsers(prev => prev.map(u => u._id === userId ? { ...u, credits: (u.credits || 0) + credits } : u));
      toast.success(`Granted ${credits} credits`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const dismissCreditRequest = async (requestId) => {
    try {
      await axios.delete(`${API}/api/auth/credit-requests/${requestId}`, { withCredentials: true });
      setCreditRequests(prev => prev.filter(r => r._id !== requestId));
      toast.success('Request dismissed');
    } catch (err) {
      toast.error('Failed to dismiss');
    }
  };

  const handleResetAndComplete = async (reqId, userId) => {
    try {
      const { data } = await axios.put(`${API}/api/auth/users/${userId}/reset-password`, {}, { withCredentials: true });
      await axios.put(`${API}/api/auth/password-reset-requests/${reqId}/complete`, {}, { withCredentials: true });
      setPasswordResetRequests(prev => prev.filter(r => r._id !== reqId));
      setResetResult({ userId, tempPassword: data.temp_password });
      toast.success('Password reset completed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const dismissPasswordReset = async (reqId) => {
    try {
      await axios.put(`${API}/api/auth/password-reset-requests/${reqId}/complete`, {}, { withCredentials: true });
      setPasswordResetRequests(prev => prev.filter(r => r._id !== reqId));
      toast.success('Request dismissed');
    } catch (err) {
      toast.error('Failed to dismiss');
    }
  };

  const completeInvoiceRequest = async (reqId) => {
    try {
      await axios.put(`${API}/api/auth/invoice-requests/${reqId}/complete`, {}, { withCredentials: true });
      setInvoiceRequests(prev => prev.filter(r => r._id !== reqId));
      toast.success('Invoice request completed');
    } catch (err) {
      toast.error('Failed to complete');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-4">
        <button onClick={onBack} className="text-slate-500 hover:text-slate-700" data-testid="admin-back-btn">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-lg font-bold text-slate-800">User Management</h1>
          <p className="text-xs text-slate-500">Manage user access, roles, and credits</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {/* Data Import Section */}
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h2 className="text-sm font-bold text-slate-800 mb-3 uppercase tracking-wider">Data Import</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Spaza Shop Data</h3>
              <p className="text-xs text-slate-500 mb-3">Upload spaza shop locations with name, address/coordinates.</p>
              <div className="flex gap-2">
                <button onClick={() => downloadTemplate('spaza')} className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-100 transition-colors" data-testid="download-spaza-template">
                  <DownloadSimple size={14} /> Template
                </button>
                <button onClick={() => spazaFileRef.current?.click()} disabled={importingSpaza} className="inline-flex items-center gap-1 text-xs font-medium text-white bg-slate-800 rounded px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50 transition-colors" data-testid="import-spaza-btn">
                  <UploadSimple size={14} /> {importingSpaza ? 'Importing...' : 'Upload'}
                </button>
                <input ref={spazaFileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleSpazaImport} className="hidden" />
              </div>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Property Valuations</h3>
              <p className="text-xs text-slate-500 mb-3">Upload properties with rent, bedrooms, bathrooms, location.</p>
              <div className="flex gap-2">
                <button onClick={() => downloadTemplate('properties')} className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-100 transition-colors" data-testid="download-props-template">
                  <DownloadSimple size={14} /> Template
                </button>
                <button onClick={() => propsFileRef.current?.click()} disabled={importingProps} className="inline-flex items-center gap-1 text-xs font-medium text-white bg-slate-800 rounded px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50 transition-colors" data-testid="import-props-btn">
                  <UploadSimple size={14} /> {importingProps ? 'Importing...' : 'Upload'}
                </button>
                <input ref={propsFileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handlePropsImport} className="hidden" />
              </div>
            </div>
          </div>
        </div>

        {/* Credit Requests */}
        {creditRequests.length > 0 && (
          <div className="bg-white rounded-lg border border-amber-200 p-5" data-testid="credit-requests-section">
            <h2 className="text-sm font-bold text-amber-800 mb-3 uppercase tracking-wider">Pending Credit Requests ({creditRequests.length})</h2>
            <div className="space-y-2">
              {creditRequests.map(cr => (
                <div key={cr._id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100" data-testid={`credit-req-${cr._id}`}>
                  <div>
                    <span className="text-sm font-medium text-slate-800">{cr.user_name || cr.user_email}</span>
                    <span className="text-xs text-slate-500 ml-2">{cr.user_email}</span>
                    <span className="text-xs text-slate-400 ml-2">{new Date(cr.created_at).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={grantValue}
                      onChange={(e) => setGrantValue(e.target.value)}
                      className="w-16 text-xs border border-slate-300 rounded px-2 py-1 text-center"
                      min="1"
                    />
                    <button
                      onClick={() => approveCreditRequest(cr._id, cr.user_id)}
                      className="text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded px-3 py-1.5 transition-colors"
                      data-testid={`approve-req-${cr._id}`}
                    >
                      Grant
                    </button>
                    <button
                      onClick={() => dismissCreditRequest(cr._id)}
                      className="text-xs font-medium text-slate-500 hover:text-red-600 transition-colors"
                      data-testid={`dismiss-req-${cr._id}`}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Password Reset Requests */}
        {passwordResetRequests.length > 0 && (
          <div className="bg-white rounded-lg border border-blue-200 p-5" data-testid="password-reset-requests-section">
            <h2 className="text-sm font-bold text-blue-800 mb-3 uppercase tracking-wider">Password Reset Requests ({passwordResetRequests.length})</h2>
            <div className="space-y-2">
              {passwordResetRequests.map(pr => (
                <div key={pr._id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100" data-testid={`pw-reset-req-${pr._id}`}>
                  <div>
                    <span className="text-sm font-medium text-slate-800">{pr.user_name || pr.email}</span>
                    <span className="text-xs text-slate-500 ml-2">{pr.email}</span>
                    <span className="text-xs text-slate-400 ml-2">{new Date(pr.created_at).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleResetAndComplete(pr._id, pr.user_id)}
                      className="text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded px-3 py-1.5 transition-colors"
                      data-testid={`reset-complete-${pr._id}`}
                    >
                      Reset Password
                    </button>
                    <button
                      onClick={() => dismissPasswordReset(pr._id)}
                      className="text-xs font-medium text-slate-500 hover:text-red-600 transition-colors"
                      data-testid={`dismiss-pw-reset-${pr._id}`}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Invoice Requests */}
        {invoiceRequests.length > 0 && (
          <div className="bg-white rounded-lg border border-indigo-200 p-5" data-testid="invoice-requests-section">
            <h2 className="text-sm font-bold text-indigo-800 mb-3 uppercase tracking-wider">Invoice Requests ({invoiceRequests.length})</h2>
            <div className="space-y-2">
              {invoiceRequests.map(ir => (
                <div key={ir._id} className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg border border-indigo-100" data-testid={`invoice-req-${ir._id}`}>
                  <div>
                    <span className="text-sm font-medium text-slate-800">{ir.user_name || ir.user_email}</span>
                    <span className="text-xs text-slate-500 ml-2">{ir.user_email}</span>
                    <span className="text-xs text-slate-400 ml-2">{new Date(ir.created_at).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => completeInvoiceRequest(ir._id)}
                      className="text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded px-3 py-1.5 transition-colors"
                      data-testid={`complete-invoice-${ir._id}`}
                    >
                      Mark Sent
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Users Table */}
        {loading ? (
          <div className="text-center py-20 text-slate-400">Loading users...</div>
        ) : (
          <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
            <table className="w-full min-w-[900px]" data-testid="users-table">
              <thead>
                <tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-5 py-3">User</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Credits</th>
                  <th className="px-4 py-3">Valuations</th>
                  <th className="px-4 py-3">Last Valuation</th>
                  <th className="px-4 py-3">Joined</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map(u => {
                  const isSelf = u._id === currentUser?.id;
                  return (
                    <tr key={u._id} className="hover:bg-slate-50" data-testid={`user-row-${u._id}`}>
                      <td className="px-5 py-3">
                        <div className="text-sm font-medium text-slate-800">{u.name || '\u2014'}</div>
                        <div className="text-xs text-slate-500">{u.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => !isSelf && toggleRole(u._id, u.role)}
                          disabled={isSelf}
                          className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${u.role === 'admin' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600'} ${isSelf ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-80'}`}
                          data-testid={`role-badge-${u._id}`}
                        >
                          {u.role === 'admin' ? <ShieldCheck size={14} /> : <User size={14} />}
                          {u.role}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => !isSelf && toggleStatus(u._id, u.is_active !== false)}
                          disabled={isSelf}
                          className={`inline-flex items-center gap-1 text-xs font-semibold ${u.is_active !== false ? 'text-emerald-600' : 'text-red-500'} ${isSelf ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          data-testid={`status-toggle-${u._id}`}
                        >
                          {u.is_active !== false ? <ToggleRight size={20} weight="fill" /> : <ToggleLeft size={20} />}
                          {u.is_active !== false ? 'Active' : 'Disabled'}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        {creditEditing === u._id ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              value={creditValue}
                              onChange={(e) => setCreditValue(e.target.value)}
                              className="w-16 text-xs border border-slate-300 rounded px-1.5 py-1 text-center"
                              min="0"
                              autoFocus
                              onKeyDown={(e) => { if (e.key === 'Enter') saveCredits(u._id); if (e.key === 'Escape') setCreditEditing(null); }}
                              data-testid={`credit-input-${u._id}`}
                            />
                            <button onClick={() => saveCredits(u._id)} className="text-emerald-600 hover:text-emerald-700 text-xs font-semibold" data-testid={`credit-save-${u._id}`}>Save</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setCreditEditing(u._id); setCreditValue(String(u.credits || 0)); }}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-amber-700 cursor-pointer"
                            title="Click to edit credits"
                            data-testid={`credit-display-${u._id}`}
                          >
                            {u.role === 'admin' ? <span className="text-slate-400">{"\u221E"}</span> : <>{u.credits || 0}</>}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <ChartBar size={14} className="text-slate-400" />
                          <span className="text-sm font-semibold text-slate-800" data-testid={`valuation-count-${u._id}`}>{u.valuation_count || 0}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500" data-testid={`last-valuation-${u._id}`}>
                        {u.last_valuation_at ? (() => {
                          const d = new Date(u.last_valuation_at);
                          return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}`;
                        })() : <span className="text-slate-300">{"\u2014"}</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : '\u2014'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {!isSelf && (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => resetPassword(u._id, u.email)}
                              className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 hover:text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded px-2 py-1 transition-colors"
                              title="Reset Password"
                              data-testid={`reset-pw-${u._id}`}
                            >
                              <Key size={13} />
                              Reset
                            </button>
                            <button
                              onClick={() => deleteUser(u._id, u.email)}
                              className="text-red-400 hover:text-red-600 transition-colors p-1"
                              title="Delete User"
                              data-testid={`delete-user-${u._id}`}
                            >
                              <Trash size={15} />
                            </button>
                          </div>
                        )}
                        {resetResult?.userId === u._id && (
                          <div className="mt-1 p-1.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800" data-testid={`temp-pw-${u._id}`}>
                            Temp: <strong className="select-all">{resetResult.tempPassword}</strong>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
